/**
 * Klaviyo Back In Stock (BIS) custom element
 * Handles back-in-stock notifications for out-of-stock products
 * @extends HTMLElement
 */
class KlaviyoBIS extends HTMLElement {
  /**
   * Creates an instance of KlaviyoBIS
   * Initializes data points and element bindings
   */
  constructor() {
    super();

    // Establish datapoints
    this.targetVariantId = this.dataset.variantId;
    this.klaviyoPublicKey = this.dataset.klaviyoPublicKey;

    // Establish element bindings
    this.modalContainer = this.querySelector('.bis-modal-container');
    this.modalTriggerButton = this.querySelector('.modal-trigger');
    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('input');
    this.successModal = this.querySelector('.alert--success');
    this.dangerModal = this.querySelector('.alert--danger');
    this.formSubmitButton = this.querySelector('.button--submit');
    this.modalOverlay = this.querySelector('.bis-modal__overlay');
    this.modalClose = this.querySelector('.modal__close');
  }

  /**
   * Handles successful form submission
   * @param {Response} result - The fetch response object
   */
  onFormSubmitSuccess(result) {
    this.formSubmitButton.removeAttribute('disabled');

    if (result.status >= 200 && result.status <= 300) {
      this.successModal.classList.add('alert--active');
      this.dangerModal.classList.remove('alert--active');
      this.form.classList.add('hidden');
      this.formSubmitButton.classList.add('hidden');
    } else {
      this.dangerModal.classList.add('alert--active');
    }
  }

  /**
   * Handles failed form submission
   * @param {Error} error - The error object
   */
  onFormSubmitFail(error) {
    this.formSubmitButton.removeAttribute('disabled');
    this.dangerModal.classList.add('alert--active');
  }

  /**
   * Handles form submission
   * Makes API call to Klaviyo BIS endpoint
   * @param {Event} e - The form submit event
   */
  onFormSubmit(e) {
    let onFormSubmitSuccess = this.onFormSubmitSuccess.bind(this);
    let onFormSubmitFail = this.onFormSubmitFail.bind(this);
    e.preventDefault();

    this.formSubmitButton.setAttribute('disabled', true);

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: `${this.emailInput.value}`,
              },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.targetVariantId}`,
            },
          },
        },
      },
    };

    var requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-06-15',
      },
      body: JSON.stringify(payload),
    };

    fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${this.klaviyoPublicKey}`,
      requestOptions
    )
      .then(onFormSubmitSuccess)
      .catch(onFormSubmitFail);
  }

  /**
   * Closes the modal
   * @param {Event} e - The close event
   */
  onModalClose(e) {
    this.modalContainer.classList.add('hidden');
  }

  /**
   * Opens the modal
   * @param {Event} e - The open event
   */
  onModalOpen(e) {
    this.modalContainer.classList.remove('hidden');
  }

  /**
   * Lifecycle callback when component is added to DOM
   * Sets up event listeners
   */
  connectedCallback() {
    let onFormSubmit = this.onFormSubmit.bind(this);
    let onModalClose = this.onModalClose.bind(this);
    let onModalOpen = this.onModalOpen.bind(this);

    this.form.addEventListener('submit', onFormSubmit);
    this.modalOverlay.addEventListener('click', onModalClose);
    this.modalClose.addEventListener('click', onModalClose);
    this.modalTriggerButton.addEventListener('click', onModalOpen);
  }

  /**
   * Lifecycle callback when component is removed from DOM
   * Cleans up event listeners
   */
  disconnectedCallback() {
    this.form.removeEventListener('submit');
    this.modalOverlay.removeEventListener('click');
    this.modalClose.removeEventListener('click');
    this.modalTriggerButton.removeEventListener('click');
  }
}

// Register the custom element
customElements.define('klaviyo-bis', KlaviyoBIS);
